import {
  type DocumentData,
  type DocumentReference,
  type Firestore,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { fuseauDe, fuseauValide } from '@taki/data-model';
import { z } from 'zod';

/**
 * CE QUE L'ORG A INSTALLE, ET OU ELLE TOURNE : deux documents de reglage de la
 * base du client, plus deux champs de `taki.config.json`.
 *
 * - `orgs/{orgId}/settings/apps` : la liste ORDONNEE des apps installees (elle
 *   decide l'ordre du dock). Lue par tout membre actif, ecrite par un owner ou
 *   un admin actif.
 * - `orgs/{orgId}/settings/environnement` : `production` ou `staging` (la bande
 *   STAGING), et le FUSEAU HORAIRE de l'org (`fuseau`, absent =
 *   Europe/Luxembourg : `docs/socle/fuseau-org.md`). Lue SANS COMPTE, comme le
 *   branding : la bande s'affiche sur l'ecran de connexion. Ecrite par l'owner
 *   seul, chaque champ sans toucher l'autre (`merge`).
 *
 * Ni la liste ni le mode ne s'ecrivent dans l'org `master` depuis un client : sa
 * liste suit le depot, posee par `pnpm dev:local` (son fuseau, lui, se regle). Pourquoi ces deux faits ont quitte
 * la config du Hosting, et l'ordre du passage des environnements existants :
 * `docs/socle/apps-installees-en-base.md`.
 *
 * DOCUMENT ABSENT = AUCUNE APP, et PRODUCTION. Aucun repli sur une autre source.
 *
 * DESINSTALLER NE TOUCHE AUCUNE DONNEE : seule la liste change. Les fiches de
 * l'app, son instance (`orgs/{orgId}/apps/{appId}`) et les droits par app des
 * membres restent en place, et reinstaller l'app les retrouve.
 *
 * Les bornes sont RECOPIEES dans `functions/src/ia/installed-apps.ts` et dans
 * les Security Rules ; `scripts/apps-en-base.test.mjs` compare les recopies.
 */

const SETTINGS_COLLECTION = 'settings';
export const APPS_INSTALLEES_DOCUMENT = 'apps';
export const ENVIRONNEMENT_DOCUMENT = 'environnement';
export const APPS_INSTALLEES_SCHEMA_VERSION = 1;
export const APPS_INSTALLEES_MAX = 64;
export const APP_ID_MOTIF = /^[a-z][a-z0-9-]{0,63}$/;

/** Les deux natures d'un deploiement. `production` est aussi le sens de l'absence. */
export const ENVIRONNEMENTS = ['production', 'staging'] as const;
export type Environnement = (typeof ENVIRONNEMENTS)[number];

export const appIdSchema = z.string().regex(APP_ID_MOTIF, "identifiant d'app invalide");
export const environnementSchema = z.enum(ENVIRONNEMENTS);

/** Les ids lisibles d'une valeur brute, dans l'ordre, sans doublon ni entree illisible. */
export function idsDApps(brut: unknown): string[] {
  if (!Array.isArray(brut)) return [];
  const vus = new Set<string>();
  for (const id of brut) if (typeof id === 'string' && APP_ID_MOTIF.test(id)) vus.add(id);
  return [...vus];
}

/** Lecture tolerante : une valeur inconnue vaut l'absence, donc la production. */
export function environnementDe(brut: unknown): Environnement {
  return brut === 'staging' ? 'staging' : 'production';
}

/** La liste apres installation : ajoutee a la fin, le reste ne bouge pas. */
export function avecApp(apps: readonly string[], appId: string): string[] {
  return apps.includes(appId) ? [...apps] : [...apps, appId];
}

/** La liste apres desinstallation : l'ordre du reste ne bouge pas. */
export function sansApp(apps: readonly string[], appId: string): string[] {
  return apps.filter((id) => id !== appId);
}

export interface AppsInstalleesRepository {
  /** La liste du document, `null` si le document n'existe pas encore. */
  lire(): Promise<string[] | null>;
  observer(surChangement: (apps: string[] | null) => void, surErreur?: (erreur: unknown) => void): () => void;
  /** Installe une app : sans effet si elle l'est deja. */
  installer(appId: string): Promise<void>;
  /** Desinstalle une app : la liste seule change, aucune donnee n'est touchee. */
  desinstaller(appId: string): Promise<void>;
}

export interface EnvironnementRepository {
  lire(): Promise<Environnement>;
  observer(surChangement: (environnement: Environnement) => void, surErreur?: (erreur: unknown) => void): () => void;
  changer(environnement: Environnement): Promise<void>;
  /** Le fuseau REGLE, `null` sans reglage (l'org vit alors en Europe/Luxembourg). */
  lireFuseau(): Promise<string | null>;
  observerFuseau(surChangement: (fuseau: string | null) => void, surErreur?: (erreur: unknown) => void): () => void;
  /** Regle le fuseau (nom IANA), ou le retire avec `null`. Owner seul (rules). */
  changerFuseau(fuseau: string | null): Promise<void>;
}

/** Lecture tolerante du reglage : un nom IANA connu, sinon `null` (pas de reglage). */
export function fuseauRegleDe(brut: unknown): string | null {
  return typeof brut === 'string' && fuseauDe(brut) === brut ? brut : null;
}

function reference(db: Firestore, orgId: string, id: string): DocumentReference {
  return doc(db, 'orgs', orgId, SETTINGS_COLLECTION, id);
}

export function createAppsInstalleesRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): AppsInstalleesRepository {
  const ref = reference(db, orgId, APPS_INSTALLEES_DOCUMENT);

  /* UNE TRANSACTION, parce que la liste est ecrite ENTIERE : deux admins qui
     installent chacun une app au meme moment liraient la meme liste, et le
     second ecraserait le geste du premier sans un mot. */
  async function modifier(appId: string, calcul: (apps: readonly string[], id: string) => string[]): Promise<void> {
    const id = appIdSchema.parse(appId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const avant = snap.exists() ? idsDApps(snap.data()['apps']) : [];
      const apres = calcul(avant, id);
      if (snap.exists() && apres.length === avant.length && apres.every((x, i) => x === avant[i])) return;
      if (apres.length > APPS_INSTALLEES_MAX) {
        throw new Error(`apps installees : au plus ${APPS_INSTALLEES_MAX} apps.`);
      }
      const data: DocumentData = {
        orgId,
        schemaVersion: APPS_INSTALLEES_SCHEMA_VERSION,
        apps: apres,
        updatedAt: serverTimestamp(),
        updatedBy: getActorId(),
      };
      tx.set(ref, data);
    });
  }

  return {
    async lire() {
      const snap = await getDoc(ref);
      return snap.exists() ? idsDApps(snap.data()['apps']) : null;
    },
    observer(surChangement, surErreur) {
      return onSnapshot(
        ref,
        (snap) => surChangement(snap.exists() ? idsDApps(snap.data()['apps']) : null),
        (erreur) => surErreur?.(erreur),
      );
    },
    installer: (appId) => modifier(appId, avecApp),
    desinstaller: (appId) => modifier(appId, sansApp),
  };
}

export function createEnvironnementRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  /** Appele a chaque lecture du fuseau : `sdk.timezone` le suit. */
  surFuseau: (fuseau: string | null) => void = () => undefined,
): EnvironnementRepository {
  const ref = reference(db, orgId, ENVIRONNEMENT_DOCUMENT);
  const fuseauDuDocument = (data: DocumentData | undefined): string | null => {
    const fuseau = fuseauRegleDe(data?.['fuseau']);
    surFuseau(fuseau);
    return fuseau;
  };
  return {
    async lire() {
      const snap = await getDoc(ref);
      return environnementDe(snap.exists() ? snap.data()['environnement'] : undefined);
    },
    observer(surChangement, surErreur) {
      return onSnapshot(
        ref,
        (snap) => {
          fuseauDuDocument(snap.data());
          surChangement(environnementDe(snap.exists() ? snap.data()['environnement'] : undefined));
        },
        (erreur) => surErreur?.(erreur),
      );
    },
    async changer(environnement) {
      await setDoc(ref, {
        orgId,
        schemaVersion: APPS_INSTALLEES_SCHEMA_VERSION,
        environnement: environnementSchema.parse(environnement),
        updatedAt: serverTimestamp(),
        updatedBy: getActorId(),
      }, { merge: true });
    },
    async lireFuseau() {
      return fuseauDuDocument((await getDoc(ref)).data());
    },
    observerFuseau(surChangement, surErreur) {
      return onSnapshot(
        ref,
        (snap) => surChangement(fuseauDuDocument(snap.data())),
        (erreur) => surErreur?.(erreur),
      );
    },
    async changerFuseau(fuseau) {
      if (fuseau !== null && !fuseauValide(fuseau)) throw new Error(`Fuseau inconnu : ${fuseau}.`);
      await setDoc(ref, {
        orgId,
        schemaVersion: APPS_INSTALLEES_SCHEMA_VERSION,
        fuseau: fuseau ?? deleteField(),
        updatedAt: serverTimestamp(),
        updatedBy: getActorId(),
      }, { merge: true });
    },
  };
}
